// src/components/airlines/Forms/FormAddEditAirline.jsx
import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import i18next from "i18next";

import useCreateAirline from "../../../hooks/airlines/useCreateAirline";
import useUpdateAirline from "../../../hooks/airlines/useUpdateAirline";
import useGetAirline from "../../../hooks/airlines/useGetAirline";
import { useUploadMedia } from "../../../hooks/media/useUploadMedia";
import useSingleImageField from "../../../hooks/media/useSingleImageField";

import FormInput from "../../inputs/FormInput";
import LanguageToggle from "../../global/LanguageToggle";

const FormAddEditAirline = ({ isEdit = false, onSubmitSuccess }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    // Hooks
    const { createAirline, isPending: isPendingCreate } = useCreateAirline();
    const { updateAirline, isPending: isPendingUpdate } = useUpdateAirline();
    const { airline, isLoading: isLoadingAirline } = useGetAirline(isEdit ? id : null);
    const { uploadMedia, isPending: isUploading } = useUploadMedia();

    // Form States
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [status, setStatus] = useState("active");
    const [errors, setErrors] = useState({});

    // Image Hook
    const img = useSingleImageField({
        initialImage: null,
        uploadMedia,
        isArabic,
    });

    useEffect(() => {
        if (isEdit && airline) {
            setNameAr(airline?.name?.ar || "");
            setNameEn(airline?.name?.en || "");
            setStatus(airline?.status || "active");

            // Sync Image
            if (airline?.image) {
                // If it's a media object (from AirlineResource)
                if (typeof airline.image === 'object' && airline.image.id) {
                    img.setFromServerImage(airline.image);
                } else {
                    // Fallback for simple string if needed
                    img.setFromServerImage({ url: airline.image, name: airline.image });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, airline]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            status,
            ...img.getImagePayload(),
        };

        if (isEdit) {
            updateAirline(
                { id, data: payload },
                {
                    onSuccess: () => {
                        if (onSubmitSuccess) onSubmitSuccess();
                        navigate("/airlines");
                    },
                    onError: (err) => setErrors(err?.response?.data?.errors || {}),
                }
            );
        } else {
            createAirline(payload, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                    navigate("/airlines");
                },
                onError: (err) => setErrors(err?.response?.data?.errors || {}),
            });
        }
    };

    if (isEdit && isLoadingAirline) return <Typography color="primary" sx={{ p: 4 }}>Loading...</Typography>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {isEdit ? (isArabic ? "تعديل شركة طيران" : "Edit Airline") : (isArabic ? "إضافة شركة طيران جديدة" : "Add New Airline")}
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Logo Upload */}
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2, flexDirection: 'column-reverse', alignItems: 'center' }}>
                            <FormInput
                                type="file"
                                label={isArabic ? "شعار شركة الطيران" : "Airline Logo"}
                                name="image"
                                value={img.imagePreview}
                                onChange={img.handleImageChange}
                                isUploading={isUploading}
                                errorMessage={img.imageError || errors.image?.[0]}
                                successMessage={img.imageName && !img.imageError ? (isArabic ? "تم رفع الصورة" : "Image uploaded") : ""}
                                width={120}
                                height={120}
                                borderRadius="8px"
                                required={false}
                                onDeleted={img.handleDeleted}
                                mediaId={img.imageId}
                            />
                        </Grid>

                        {/* Basic Info */}
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="أدخل اسم الشركة بالعربي"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    error={errors["name.ar"] || errors.name_ar}
                                    required
                                />
                            </Grid>
                        )}
                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Name (English)"
                                    placeholder="Enter airline name in English"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    error={errors["name.en"] || errors.name_en}
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="select"
                                label={isArabic ? "الحالة" : "Status"}
                                value={status}
                                options={[
                                    { id: "active", name: isArabic ? "نشط" : "Active" },
                                    { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
                                ]}
                                onChange={(e) => setStatus(e.target.value)}
                                error={errors.status}
                                required
                            />
                        </Grid>
                    </Grid>

                    {/* Actions */}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/airlines")}
                            sx={{ minWidth: 120, borderRadius: 2 }}
                        >
                            {isArabic ? "إلغاء" : "Cancel"}
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isPendingCreate || isPendingUpdate || isUploading}
                            sx={{ minWidth: 150, borderRadius: 2 }}
                        >
                            {(isPendingCreate || isPendingUpdate) ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                isEdit ? (isArabic ? "تعديل" : "Update") : (isArabic ? "إضافة" : "Add")
                            )}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default FormAddEditAirline;
